import { useCallback, useEffect, useMemo, useRef } from "react";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  buildAdvertiserInstagramIdentityPatch,
  facebookPageWithCatalogLinkedMetadata,
  instagramSelectOptionsFromAccounts,
  resolveAdvertiserInstagramAfterPageFetch,
  resolveAdvertiserInstagramOnFacebookPagePicked,
  shouldDeferAdvertiserInstagramPageSync,
} from "../utils/creativesWithInstagramFromFetch";
import { mergeInstagramAccountsWithPageScopedFirst } from "../utils/mergeInstagramAccounts";
import { usePageScopedInstagramAccounts } from "./usePageScopedInstagramAccounts";

type UseAdvertiserInstagramPageSyncArgs = {
  creative: CreativeBlock;
  catalogPages: FacebookPage[];
  catalogAccounts: InstagramAccount[];
  onFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
  /** Guided create: default to first fetched actor when the page has no linked IG. */
  applyDefaultOnFacebookPageChange?: boolean;
  instagramClearDisabled?: boolean;
  onPatch: (patch: Partial<CreativeBlock>) => void;
};

/**
 * Keeps advertiser Facebook page + Instagram identity in sync with page-scoped Meta
 * fetches. Manual Instagram picks are preserved until the Facebook page changes.
 */
export function useAdvertiserInstagramPageSync({
  creative,
  catalogPages,
  catalogAccounts,
  onFacebookPageChange,
  applyDefaultOnFacebookPageChange = false,
  instagramClearDisabled = false,
  onPatch,
}: UseAdvertiserInstagramPageSyncArgs) {
  const userOverrodeInstagramPageRef = useRef<string | null>(null);
  const lastSyncedPageIdRef = useRef<string | null>(null);
  const creativeRef = useRef(creative);
  creativeRef.current = creative;

  const pageScopedAccounts = usePageScopedInstagramAccounts(
    true,
    creative.fbPage?.id,
    onFacebookPageChange
  );

  const mergedInstagramOptions = useMemo(
    () =>
      instagramSelectOptionsFromAccounts(
        mergeInstagramAccountsWithPageScopedFirst(
          pageScopedAccounts,
          catalogAccounts
        ),
        creative.instagramAccount
      ),
    [catalogAccounts, creative.instagramAccount, pageScopedAccounts]
  );

  const applyResolvedInstagram = useCallback(
    (page: FacebookPage, resolved: InstagramAccount | null) => {
      const patch = buildAdvertiserInstagramIdentityPatch({
        page,
        creative: creativeRef.current,
        instagramAccount: resolved,
      });
      if (patch) {
        onPatch(patch);
      }
    },
    [onPatch]
  );

  useEffect(() => {
    const current = creativeRef.current;
    const pageId = current.fbPage?.id?.trim();
    const page = current.fbPage;
    if (!pageId || !page || !onFacebookPageChange) {
      lastSyncedPageIdRef.current = null;
      return;
    }

    const previousSyncedPageId = lastSyncedPageIdRef.current;
    const pageIdChanged =
      previousSyncedPageId !== null && pageId !== previousSyncedPageId;
    if (pageId !== previousSyncedPageId) {
      if (previousSyncedPageId !== null) {
        userOverrodeInstagramPageRef.current = null;
      }
      lastSyncedPageIdRef.current = pageId;
    }

    const userOverrodeForPage =
      userOverrodeInstagramPageRef.current === pageId;
    const hasSelectedInstagram = Boolean(current.instagramAccount?.id);

    if (
      shouldDeferAdvertiserInstagramPageSync({
        pageIdChanged,
        fetchedAccountsEmpty: pageScopedAccounts.length === 0,
        hasSelectedInstagram,
        defaultToFirstFetchedWhenUnlinked: applyDefaultOnFacebookPageChange,
        userOverrodeForPage,
      })
    ) {
      return;
    }

    const enrichedPage = facebookPageWithCatalogLinkedMetadata(
      page,
      catalogPages
    );
    const result = resolveAdvertiserInstagramAfterPageFetch({
      page: enrichedPage,
      creative: { ...current, fbPage: enrichedPage },
      catalogAccounts,
      fetchedAccounts: pageScopedAccounts,
      userOverrodeForPage,
      instagramClearDisabled,
      defaultToFirstFetchedWhenUnlinked: applyDefaultOnFacebookPageChange,
    });

    if (result.action === "skip") {
      return;
    }

    applyResolvedInstagram(enrichedPage, result.instagramAccount);
  }, [
    applyDefaultOnFacebookPageChange,
    applyResolvedInstagram,
    catalogAccounts,
    catalogPages,
    creative.fbPage?.id,
    creative.instagramAccount?.id,
    instagramClearDisabled,
    onFacebookPageChange,
    pageScopedAccounts,
  ]);

  const handleFacebookPageChange = useCallback(
    (selectedPage: FacebookPage | null) => {
      if (!selectedPage) {
        lastSyncedPageIdRef.current = null;
        onPatch({
          fbPage: null,
          ...(instagramClearDisabled ? {} : { instagramAccount: null }),
        });
        return;
      }

      const nextPageId = selectedPage.id?.trim() ?? "";
      if (nextPageId !== creative.fbPage?.id?.trim()) {
        userOverrodeInstagramPageRef.current = null;
      }

      const { page, instagramAccount } =
        resolveAdvertiserInstagramOnFacebookPagePicked({
          page: selectedPage,
          catalogPages,
          catalogAccounts,
          instagramClearDisabled,
        });
      onPatch({
        fbPage: page,
        ...(instagramClearDisabled ? {} : { instagramAccount }),
      });
    },
    [
      catalogAccounts,
      catalogPages,
      creative.fbPage?.id,
      instagramClearDisabled,
      onPatch,
    ]
  );

  const handleInstagramChange = useCallback(
    (instagramAccount: InstagramAccount | null) => {
      if (instagramClearDisabled && !instagramAccount) {
        return;
      }
      userOverrodeInstagramPageRef.current =
        creative.fbPage?.id?.trim() ?? null;
      onPatch({ instagramAccount });
    },
    [creative.fbPage?.id, instagramClearDisabled, onPatch]
  );

  return {
    mergedInstagramOptions,
    handleFacebookPageChange,
    handleInstagramChange,
  };
}
